import React, { useEffect, useMemo, useState } from "react";
import { Typography, Stack, Box, Button, ToggleButtonGroup, ToggleButton } from "@mui/material";
import { useNavigate } from "react-router-dom";
import { ParentContainer } from "../components/shared/sharedVisualElements";
import { getRecentAttempts } from "../apis/APICalls";
import { useApiCall } from '../hooks/useApiCall';
import { TablePresentation } from "../components/PresentationTable";
import { SummaryCard, MetricCard } from "../components/SummaryWidget";
import { CumulativeLineChart, TradesBarChart, TimeframeChart } from "../components/LineChart";
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { Fab } from '@mui/material';


export const RecentTrades=()=>{

    const [trades, setTrades] = useState([]);
    const [filter, setFilter] = useState('all');
    const navigate = useNavigate();
    const {
        executeApiCall
    } = useApiCall();

    useEffect(() => { 
        fetchAttempts();
    }, []);

    const fetchAttempts = async () => {
        await executeApiCall(() => getRecentAttempts(), {
            onSuccess: (response) => {
                setTrades(response);
            }
    });
}

    const filteredTrades = useMemo(() => {
        if (filter === 'wins') return trades.filter(t => t.pl >= 0);
        if (filter === 'losses') return trades.filter(t => t.pl < 0);
        return trades;
    }, [trades, filter]);

    const stats = useMemo(() => {
        const count = trades.length;
        const wins = trades.filter(t => t.pl >= 0).length;
        const losses = trades.filter(t => t.pl < 0).length;
        const winRate = count ? Math.round((wins / count) * 100) : 0;
        const avgWin = wins ? (trades.filter(t => t.pl >= 0).reduce((s, t) => s + t.pl, 0) / wins) : 0;
        const avgLoss = losses ? (trades.filter(t => t.pl < 0).reduce((s, t) => s + t.pl, 0) / losses) : 0;
        return { count, wins, losses, winRate, avgWin, avgLoss };
    }, [trades]);

    const exportCSV = () => {
        const rows = trades.map(t => ({ ...t }));
        const header = Object.keys(rows[0] || {});
        const csv = [header.join(','), ...rows.map(r => header.map(h => JSON.stringify(r[h] ?? '')).join(','))].join('\n');
        const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = 'recent_trades.csv';
        link.click();
        URL.revokeObjectURL(url);
    };

    return(
        <ParentContainer>

            {/* <PageTitle/> */}
            <Fab color="primary" aria-label="Back to Practice" onClick={() => navigate('/')} sx={{ position: 'fixed', bottom: 24, right: 24, zIndex: 1500 }}>
                <ArrowBackIcon />
            </Fab>

            <Stack spacing={3} sx={{ width: '100%' }}>
                {/* Key Metrics Row */}
                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} sx={{ width: '100%' }}>
                    <SummaryCard data={trades} field="pl" title="Total P/L" />
                    <SummaryCard data={trades} field="pl" aggregationType="count" title="Trades" conditionalFormat={false} />
                    <MetricCard title="Win Rate" value={`${stats.winRate}%`} subtitle={`${stats.wins}W / ${stats.losses}L`} color={stats.winRate >= 50 ? 'success.main' : 'error.main'} />
                    <MetricCard title="Avg Win" value={`${stats.avgWin.toFixed(1)}`} />
                    <MetricCard title="Avg Loss" value={`${stats.avgLoss.toFixed(1)}`} />
                </Stack>

                {/* Controls */}
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 2 }}>
                    <Typography variant="h6" sx={{ fontWeight: 600 }}>Performance Analysis</Typography>
                    <Stack direction="row" spacing={1} sx={{ flexWrap: 'wrap' }}>
                        <ToggleButtonGroup size="small" value={filter} exclusive onChange={(e, v) => v && setFilter(v)}>
                            <ToggleButton value="all">All</ToggleButton>
                            <ToggleButton value="wins">Wins</ToggleButton>
                            <ToggleButton value="losses">Losses</ToggleButton>
                        </ToggleButtonGroup>
                        <Button variant="outlined" size="small" onClick={exportCSV}>Export CSV</Button>
                    </Stack>
                </Box>

                {/* Charts Row */}
                <Stack direction={{ xs: 'column', lg: 'row' }} spacing={2}>
                    <Box sx={{ flex: 1 }}>
                        <CumulativeLineChart data={filteredTrades} field="pl" title="Cumulative P/L" height={200} formatDecimals={0} />
                    </Box>
                    <Box sx={{ flex: 1 }}>
                        <TimeframeChart data={filteredTrades} />
                    </Box>
                </Stack>

                {/* Trades Table */}
                <Box>
                    <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>Recent Trades</Typography>
                    <TablePresentation data={filteredTrades} dateFields={["created_on"]} />
                </Box>
            </Stack>

        </ParentContainer>);
}

const PageTitle=()=>{
    return(
    <Typography variant="h3" compact="h3">
        Recent Trades
    </Typography>
    );
}